import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@/lib/zodcompat";
import { z } from "zod";
import { Dialog } from "@base-ui/react/dialog";
import { ComboboxSelect } from "@/components/form/combobox-select";
import { MoneyInput } from "@/components/form/money-input";
import { PercentInput } from "@/components/form/percent-input";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/loading-button";
import { useDrivers } from "@/hooks/useDrivers";
import { useVehicles } from "@/hooks/useVehicles";
import { useAssignFreight } from "@/hooks/useFreights";
import { cn } from "@/lib/utils";

const assignSchema = z.object({
  driverId: z.string().min(1, "Motorista obrigatório"),
  vehicleId: z.string().min(1, "Veículo obrigatório"),
  driverValue: z.coerce
    .number({ error: "Valor inválido" })
    .positive("Valor deve ser positivo"),
  advancePercent: z.coerce.number().min(0).max(100).default(70),
});

type AssignFormData = z.infer<typeof assignSchema>;

interface AssignFreightModalProps {
  freightId: string;
  freightValue: number | null;
  initialDriverValue?: number | null;
  initialAdvancePercent?: number | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function formatMoney(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

export function AssignFreightModal({
  freightId,
  freightValue,
  initialDriverValue,
  initialAdvancePercent,
  open,
  onClose,
  onSuccess,
}: AssignFreightModalProps) {
  const { data: driversData } = useDrivers({ active: true, perPage: 100 });
  const { data: vehiclesData } = useVehicles({ active: true, perPage: 100 });
  const { mutate: assign, isPending } = useAssignFreight(freightId);

  const hasInitialDriverValue =
    initialDriverValue !== null &&
    initialDriverValue !== undefined &&
    initialDriverValue > 0;
  const suggestedFromFreight =
    !hasInitialDriverValue && freightValue && freightValue > 0
      ? Math.round(freightValue * 70) / 100
      : null;
  const defaultAdvancePercent =
    initialAdvancePercent !== null && initialAdvancePercent !== undefined
      ? initialAdvancePercent
      : 70;

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<AssignFormData>({
    resolver: zodResolver(assignSchema),
    defaultValues: {
      advancePercent: defaultAdvancePercent,
      driverValue: hasInitialDriverValue
        ? (initialDriverValue as number)
        : suggestedFromFreight !== null
        ? (suggestedFromFreight as number)
        : (undefined as unknown as number),
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        advancePercent: defaultAdvancePercent,
        driverValue: hasInitialDriverValue
          ? (initialDriverValue as number)
          : suggestedFromFreight !== null
          ? (suggestedFromFreight as number)
          : (undefined as unknown as number),
      });
    } else {
      reset({ advancePercent: 70 });
    }
  }, [
    open,
    reset,
    hasInitialDriverValue,
    initialDriverValue,
    suggestedFromFreight,
    defaultAdvancePercent,
  ]);

  const driverValue = watch("driverValue") || 0;
  const advancePercent = watch("advancePercent") ?? 70;

  const advanceAmount = driverValue * (advancePercent / 100);
  const balanceAmount = driverValue * (1 - advancePercent / 100);
  const showMargin = freightValue !== null && freightValue > 0;
  const margin = showMargin ? (freightValue as number) - driverValue : 0;
  const marginPct = showMargin ? (margin / (freightValue as number)) * 100 : 0;

  const driverOptions =
    driversData?.data.map((d) => ({ value: d.id, label: d.name })) ?? [];
  const vehicleOptions =
    vehiclesData?.data.map((v) => ({
      value: v.id,
      label: [v.plate, v.brand, v.model].filter(Boolean).join(" — "),
    })) ?? [];

  function onSubmit(data: AssignFormData) {
    assign(
      {
        driverId: data.driverId,
        vehicleId: data.vehicleId,
        driverValue: data.driverValue,
        advancePercent: data.advancePercent,
      },
      {
        onSuccess: () => {
          onSuccess();
          onClose();
        },
      },
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-md">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            Atribuir Motorista e Veículo
          </Dialog.Title>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            {/* Motorista */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Motorista <span className="text-destructive">*</span>
              </label>
              <Controller
                name="driverId"
                control={control}
                render={({ field }) => (
                  <ComboboxSelect
                    options={driverOptions}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    placeholder="Selecione o motorista"
                    searchPlaceholder="Buscar motorista..."
                    aria-invalid={!!errors.driverId}
                  />
                )}
              />
              {errors.driverId && (
                <p className="text-xs text-destructive">{errors.driverId.message}</p>
              )}
            </div>

            {/* Veículo */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Veículo <span className="text-destructive">*</span>
              </label>
              <Controller
                name="vehicleId"
                control={control}
                render={({ field }) => (
                  <ComboboxSelect
                    options={vehicleOptions}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    placeholder="Selecione o veículo"
                    searchPlaceholder="Buscar veículo..."
                    aria-invalid={!!errors.vehicleId}
                  />
                )}
              />
              {errors.vehicleId && (
                <p className="text-xs text-destructive">{errors.vehicleId.message}</p>
              )}
            </div>

            {/* Valores */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Valor do Motorista <span className="text-destructive">*</span>
                </label>
                <MoneyInput
                  {...register("driverValue")}
                  error={!!errors.driverValue}
                  placeholder="0,00"
                />
                {hasInitialDriverValue && !errors.driverValue && (
                  <p className="text-xs text-muted-foreground">
                    Pré-preenchido do cadastro do frete. Confirme ou ajuste.
                  </p>
                )}
                {!hasInitialDriverValue && suggestedFromFreight !== null && !errors.driverValue && (
                  <p className="text-xs text-muted-foreground">
                    Sugerido: 70% do valor do frete. Ajuste se necessário.
                  </p>
                )}
                {errors.driverValue && (
                  <p className="text-xs text-destructive">{errors.driverValue.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  % Adiantamento
                </label>
                <PercentInput
                  {...register("advancePercent")}
                  error={!!errors.advancePercent}
                />
              </div>
            </div>

            {/* Preview */}
            {driverValue > 0 && (
              <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Preview dos Repasses
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Adiantamento ({advancePercent}%)</p>
                    <p className="text-sm font-medium text-foreground">{formatMoney(advanceAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Saldo ({100 - advancePercent}%)</p>
                    <p className="text-sm font-medium text-foreground">{formatMoney(balanceAmount)}</p>
                  </div>
                </div>
                {showMargin && (
                  <div className="border-t border-border pt-2">
                    <p className="text-xs text-muted-foreground">Margem</p>
                    <p className={cn(
                      "text-sm font-semibold",
                      margin >= 0 ? "text-green-700" : "text-red-600"
                    )}>
                      {formatMoney(margin)}{" "}
                      <span className="font-normal">({marginPct.toFixed(1)}%)</span>
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isPending}
              >
                Cancelar
              </Button>
              <LoadingButton
                type="submit"
                loading={isPending}
                loadingText="Atribuindo…"
              >
                Atribuir
              </LoadingButton>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
